/**
 * Dashboard.tsx
 *
 * Purpose: Main dashboard page for Mythbound Adventure Studio™.
 * Created: 2025-01-19
 * Modified: 2026-02-23
 *
 * WHO: Authenticated users accessing their campaign hub.
 * WHAT: Displays campaigns, quick actions, and activity feed.
 * WHERE: Mythbound.App/src/pages/Dashboard.tsx
 * WHY: Central navigation point for all user activities.
 *
 * ISSUE #015 FIX: Wired Quick Action buttons with navigation and modals
 *
 * INTEGRATION POINTS:
 * - CampaignService: For fetching real campaign data (TODO)
 * - CharacterCreationModal: For "New Character" quick action
 * - ActivityService: For fetching real activity data (TODO)
 */

import React from 'react';
import {
  Box,
  Text,
  Flex,
  Heading,
  Button,
  HStack,
  VStack,
  SimpleGrid,
  useDisclosure
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import { useAuth, useInviteReturnPath } from '../context/AuthContext';
import { useCampaignContext } from '../context/CampaignContext';
import ScreenNameModal from '../components/ScreenNameModal';
import { CharacterCreationModal } from '../components/campaign/CharacterCreationModal';
import { DashboardCampaignSection } from '../components/dashboard/DashboardCampaignSection';

/**
 * Dashboard Component
 * 
 * Implements the Dashboard page template as specified in the UI/UX requirements.
 * Features:
 * - Current campaigns/sessions cards
 * - Quick action buttons (large, glowing dice icons)
 * - Recent activity timeline (scroll appearance)
 */

// Quick Action Button Component
interface QuickActionProps {
  icon: string;
  label: string;
  onClick?: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, label, onClick }) => {
  return (
    <VStack
      as={Button}
      variant="unstyled"
      height="120px"
      bg="rgba(59, 232, 255, 0.05)"
      borderRadius="md"
      justifyContent="center"
      alignItems="center"
      spacing={2}
      transition="all 0.3s"
      _hover={{
        bg: 'rgba(59, 232, 255, 0.1)',
        boxShadow: mythboundTheme.shadows.glow,
      }}
      onClick={onClick}
    >
      <Box
        fontSize="3xl"
        bg="rgba(59, 232, 255, 0.1)"
        borderRadius="full"
        p={3}
        boxShadow={mythboundTheme.shadows.glow}
      >
        {icon}
      </Box>
      <Text fontFamily={mythboundTheme.typography.fontFamily.heading}>
        {label}
      </Text>
    </VStack>
  );
};

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================

export const Dashboard: React.FC = () => {
  const { userInfo } = useAuth();
  const { currentCampaign } = useCampaignContext();
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // MODAL DISCLOSURES
  // ---------------------------------------------------------------------------
  const {
    isOpen: isScreenNameModalOpen,
    onOpen: onScreenNameModalOpen,
    onClose: onScreenNameModalClose
  } = useDisclosure();

  const {
    isOpen: isCharacterModalOpen,
    onOpen: onCharacterModalOpen,
    onClose: onCharacterModalClose
  } = useDisclosure();

  // ---------------------------------------------------------------------------
  // Check if user needs to create screen name and show modal
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (userInfo?.needsScreenName) {
      onScreenNameModalOpen();
    }
  }, [userInfo?.needsScreenName, onScreenNameModalOpen]);

  // PRD001A 001A.6 item 3: Dashboard is the backend OAuth callback's fixed
  // landing page (src/auth/config.py's frontend_redirect_url) — so this is
  // where a pending invite (stored by InviteLanding.tsx before sending the
  // user to /login) actually gets resumed after a real login round-trip.
  // No-ops until both authStatus === 'authenticated' AND needsScreenName is
  // false, i.e. AFTER the screen-name modal above is done.
  useInviteReturnPath();

  // ---------------------------------------------------------------------------
  // QUICK ACTION HANDLERS
  // Issue #015 Fix: Wire up Quick Action button click handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle "New Session" quick action.
   * Updated 2026-02-24: Redirects to /campaigns instead of /session/new.
   * Session creation requires an active campaign — user must pick one first.
   * /session/new still maps to NotFound, so this prevents a dead-end.
   *
   * INTEGRATION POINT: When SessionCreation page is implemented:
   * 1. Create /pages/SessionCreation.tsx
   * 2. Add route to App.tsx
   * 3. Update this handler to navigate('/session/new')
   */
  const handleNewSession = () => {
    navigate('/campaigns');
  };

  /**
   * Handle "New Campaign" quick action.
   * Navigates to the Campaign Builder page (already implemented).
   */
  const handleNewCampaign = () => {
    navigate('/campaign/builder');
  };

  /**
   * Handle "New Character" quick action.
   * Opens the CharacterCreationModal.
   *
   * Note: CharacterCreationModal requires a campaign_id.
   * For dashboard, we show the modal which prompts user to select a campaign.
   */
  const handleNewCharacter = () => {
    onCharacterModalOpen();
  };

  /**
   * Handle "Join Voice" quick action.
   * Navigates to voice page (shows 404/Coming Soon until implemented).
   *
   * INTEGRATION POINT: When Voice Chat feature is implemented:
   * 1. Create /pages/VoiceChat.tsx or integrate with existing VoiceBridge
   * 2. Add route to App.tsx
   * 3. This handler will automatically work
   */
  const handleJoinVoice = () => {
    navigate('/voice');
  };

  return (
    <Box>
      <Flex justify="flex-start" align="center" mb={6}>
        <Heading
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.primary.gold}
        >
          Dashboard
        </Heading>
      </Flex>

      {/* Quick Actions */}
      <Box mb={8}>
        <Heading
          size="md"
          mb={4}
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.secondary.white}
        >
          Quick Actions
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <QuickAction icon="" label="New Session" onClick={handleNewSession} />
          <QuickAction icon="" label="New Campaign" onClick={handleNewCampaign} />
          <QuickAction icon="" label="New Character" onClick={handleNewCharacter} />
          <QuickAction icon="" label="Join Voice" onClick={handleJoinVoice} />
        </SimpleGrid>
      </Box>

      {/* Campaigns - Real Data with Pagination */}
      <Box mb={8}>
        <DashboardCampaignSection />
      </Box>
      
      {/* ===================================================================
          MODALS
      =================================================================== */}

      {/* Screen Name Modal - shown when user needs to set screen name */}
      <ScreenNameModal
        isOpen={isScreenNameModalOpen}
        onClose={onScreenNameModalClose}
      />

      {/* ===============================================================
          Character Creation Modal
          Issue #015 Fix: Added for "New Character" quick action

          Updated 2026-02-23: campaignId sourced from CampaignContext
          instead of hardcoded empty string. If no campaign is selected,
          the modal will show a prompt to select one first.
      =============================================================== */}
      <CharacterCreationModal
        isOpen={isCharacterModalOpen}
        onClose={onCharacterModalClose}
        campaignId={currentCampaign?.campaign_id ?? ''}
        onCharacterCreated={() => {
          onCharacterModalClose();
          // INTEGRATION POINT: Refresh dashboard data after character creation
          // Example: refetchCampaigns() or show success toast
        }}
      />
    </Box>
  );
};

export default Dashboard;
